import { apiClient } from '../client';
import type { components } from '../schema.gen';

export type BacktestRunRequest = components['schemas']['BacktestRunRequest'];
export type BacktestRunCreateResponse = components['schemas']['BacktestRunCreateResponse'];
export type BacktestRunListResponse = components['schemas']['BacktestRunListResponse'];
export type BacktestRunRead = components['schemas']['BacktestRunRead'];
export type BacktestRunDetailRead = components['schemas']['BacktestRunDetailRead'];
export type BacktestTradeRead = components['schemas']['BacktestTradeRead'];
export type BacktestEquityCurveResponse =
  components['schemas']['services__api__schemas__backtest__EquityCurveResponse'];
export type EquityCurvePoint = components['schemas']['EquityCurvePoint'];

// ── Phase 5 schema extensions (optional — backend may not yet provide these) ──

export type WalkForwardRegime = 'bull' | 'bear' | 'chop' | 'vol_spike';

export interface WalkForwardWindow {
  start: string;
  end: string;
  label?: string;
  regime: WalkForwardRegime;
  sharpe: string;
  return_pct: string;
  passes_60_pct_gate: boolean;
}

export interface WalkForwardData {
  stability_score: string; // 0..1 decimal string
  is_sharpe: string;
  oos_sharpe: string;
  windows: WalkForwardWindow[];
}

export type VerdictState = 'pass' | 'caution' | 'fail';

export interface VerdictGate {
  key: 'data_coverage' | 'trade_count' | 'walk_forward_stability' | 'cost_sensitivity';
  passes: boolean;
  threshold: string;
  actual: string;
}

export interface VerdictData {
  state: VerdictState;
  rationale: string;
  gates: VerdictGate[];
}

export interface BacktestAnnotation {
  id: string;
  run_id: number;
  body: string;
  pin_timestamp: string | null;
  created_at: string;
  created_by: { user_id: string; name: string; role: string };
}

export interface BacktestAnnotationCreate {
  body: string;
  pin_timestamp?: string | null;
}

/** Phase 5 detail extension. Backend may add these inline on
 * BacktestRunDetailRead later; for now treat as opt-in. */
export interface BacktestRunDetailExtended {
  verdict?: VerdictData;
  walk_forward?: WalkForwardData;
}

export interface BacktestListParams {
  limit?: number;
  offset?: number;
  [key: string]: unknown;
}

export async function runBacktest(body: BacktestRunRequest): Promise<BacktestRunCreateResponse> {
  const { data } = await apiClient.post<BacktestRunCreateResponse>('/api/v1/backtests/run', body);
  return data;
}

export async function getBacktestRuns(
  params: BacktestListParams = {},
): Promise<BacktestRunListResponse> {
  const { data } = await apiClient.get<BacktestRunListResponse>('/api/v1/backtests/runs', {
    params,
  });
  return data;
}

export async function getBacktestRun(id: number): Promise<BacktestRunDetailRead> {
  const { data } = await apiClient.get<BacktestRunDetailRead>(`/api/v1/backtests/runs/${id}`);
  return data;
}

export async function getBacktestEquityCurve(id: number): Promise<BacktestEquityCurveResponse> {
  const { data } = await apiClient.get<BacktestEquityCurveResponse>(
    `/api/v1/backtests/runs/${id}/equity-curve`,
  );
  return data;
}

export interface BacktestComparisonResponse {
  runs: BacktestRunDetailRead[];
}

export async function getBacktestComparison(ids: number[]): Promise<BacktestComparisonResponse> {
  const { data } = await apiClient.get<BacktestComparisonResponse>('/api/v1/backtests/compare', {
    params: { ids: ids.join(',') },
  });
  return data;
}

// ── Annotations (Identity rule 6 — append-only, audit-grade) ──────────────────

export async function getBacktestAnnotations(runId: number): Promise<BacktestAnnotation[]> {
  try {
    const { data } = await apiClient.get<BacktestAnnotation[]>(
      `/api/v1/backtests/runs/${runId}/annotations`,
    );
    return data;
  } catch (err) {
    // Annotation endpoint is a Phase 5 backend addition. Return empty list
    // gracefully so the UI surfaces an empty composer instead of an error.
    if (
      err &&
      typeof err === 'object' &&
      'response' in err &&
      (err as { response?: { status?: number } }).response?.status === 404
    ) {
      return [];
    }
    throw err;
  }
}

export async function createBacktestAnnotation(
  runId: number,
  body: BacktestAnnotationCreate,
): Promise<BacktestAnnotation> {
  const { data } = await apiClient.post<BacktestAnnotation>(
    `/api/v1/backtests/runs/${runId}/annotations`,
    body,
  );
  return data;
}
